import React, { useEffect } from "react";
import { Controller, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Label from "./fromLabel";
import SelectBox from "./selectBox";
import { useCommonApi } from "../../../store/api/hooks/commonApiHook";

import { getSelectedOption, getSelectedOptions } from "../../../_common/functions";
import { getLookupDisplayValue, getCustomerGroupDisplayName, getRoleDisplayName, getCountryDisplayName, getCurrencyDisplayName, getTimezoneDisplayName } from "../../../_common/hookFunction";
import {
    useLookupValuesSelector,
    useLocationsSelector,
    useCountriesSelector,
    useCurrencyListSelector,
    useTimezoneSelector,
    useReturnReasonsSelector
} from "../../../store/selectors/masterSelector";
import { useCategoriesSelector } from "../../../store/selectors/categoriesSelector";
import { useUnitsSelector } from "../../../store/selectors/unitsSelector";
import { useBrandsSelector } from "../../../store/selectors/brandsSelector";
import { useTagsSelector } from "../../../store/selectors/tagsSelector";
import { useVendorsSelector } from "../../../store/selectors/vendorsSelector";
import { useCustomerGroupsSelector } from "../../../store/selectors/customerSelector";
import { useRolesSelector } from "../../../store/selectors/rolesSelector";
import { usePayTypesSelector } from "../../../store/selectors/paytypesSelector";
import { useEmployeeTypesSelector } from "../../../store/selectors/employeeTypesSelector";
import { useUserListSelector } from "../../../store/selectors/userSelector";
import { useGiftCardsCategoriesSelector } from "../../../store/selectors/giftCardsSelector";

interface CommonDropdownProps {
    name: string; // Name of the field
    placeholder: string; // Placeholder for the SelectBox
    topLabel?: string; // Optional top label
    label: string; // Label for the field
    className?: string; // Additional class names
    isMulti?: boolean; // Allow multiple selections
    control: any; // React Hook Form control
    setValue: any; // React Hook Form setValue function
    rules?: any; // Validation rules
    type:
        | "location"
        | "category"
        | "unit"
        | "brand"
        | "tag"
        | "vendor"
        | "lookup"
        | "country"
        | "state"
        | "categorytreebind" // Type of search
        | "customerGroups"
        | "allCustomerGroups"
        | "giftCardCategory"
        | "roles" //added support for roles dropdown
        | "paytypes" //added support for paytypes dropdown
        | "status" // Custom type for status dropdown
        | "employeeTypes" // added support for employeeTypes dropdown
        | "users" // Users dropdown
        | "currency" // Currency dropdown
        | "timezone" // Timezone dropdown
        | "vendorwithnovendor" // Vendor dropdown with "No Vendor" option
        | "returnReasons" // Return reasons dropdown
        | "tagwithnotag" // Tag dropdown with "No Tag" option
        | "brandwithnobrand" // Brand dropdown with "No Brand" option
        | "allLocations" // New type to fetch all lookups based on provided data
        | "withoutOwnerAdminRoles" // Roles dropdown excluding Owner Admin
        | "withoutOwnerAdminUsers"; // Users dropdown excluding Owner Admin users
    data?: any; // ID for lookup type
    preSelectValues?: any; // Pre-selected values
    topParentStyle?: React.CSSProperties; // Style for the top parent container
    onDocument?: boolean; // Whether to attach menu to document body
    size?: "sm" | "md" | "lg"; // Size of the SelectBox
    postoptions?: Array<any>;
    disabled?: boolean; // Disable the dropdown
    required?: boolean;
    labelisElipsis?: boolean; // Whether to apply text ellipsis to the label
    hideErrorMessage?: boolean;
    showCheckboxes?: boolean; // New prop to enable checkboxes
    checkboxStyle?: 'default' | 'custom'; // Optional styling variant
    onChange?: (value: any) => void; // Custom onChange handler
    isClearable?: boolean;
}

const CommonDropdown: React.FC<CommonDropdownProps> = ({
    name,
    placeholder,
    topLabel,
    label,
    className = "",
    isMulti = false,
    control,
    setValue,
    rules,
    type,
    data = null,
    preSelectValues = null,
    topParentStyle = {},
    onDocument = false,
    size = "md",
    postoptions = [],
    disabled = false,
    required = false,
    labelisElipsis = false,
    hideErrorMessage = false,
    showCheckboxes = false,
    onChange: customOnChange,
    isClearable = true,
}) => {
    const commonApi = useCommonApi();
    const { t, i18n } = useTranslation("lookup");
    const [options, setOptions] = React.useState<any[]>([]);
    const lookups = useLookupValuesSelector();
    const locations = useLocationsSelector();
    const categories = useCategoriesSelector();
    const units = useUnitsSelector();
    const brands = useBrandsSelector();
    const tags = useTagsSelector();
    const vendors = useVendorsSelector();
    const countries = useCountriesSelector();
    const customerGroups = useCustomerGroupsSelector();
    const giftCardCategories = useGiftCardsCategoriesSelector();
    const roles = useRolesSelector();
    const payTypes = usePayTypesSelector();
    const employeeTypes = useEmployeeTypesSelector();
    const userList = useUserListSelector();
    const currencyList = useCurrencyListSelector();
    const timezoneList = useTimezoneSelector();
    const returnReasons = useReturnReasonsSelector();

    // Read via useWatch rather than control._formValues[name] — the latter is a
    // flat key access and misses nested field names like "rules.0.fkTypeId".
    const selectedValue = useWatch({ control, name });

    const [preSelectApplied, setPreSelectApplied] = React.useState<boolean>(false);
    
    const lastPreSelectRef = React.useRef<any>(null);

    const processLocations = () => {
        if (locations && locations.length > 0) {
            const activeLocations = locations?.filter((loc: any) => loc.active);
            setOptions(
                activeLocations.map((loc: any) => ({
                    label: loc.locationName,
                    value: loc.locationId,
                    locDetails: loc,
                }))
            );
        }
    };

     const processAllLocations = () => {
        if (locations && locations.length > 0) {
            setOptions(
                locations.map((loc: any) => ({
                    label: loc.locationName,
                    value: loc.locationId,
                    locDetails: loc,
                }))
            );
        }
    };

    const processCategories = () => {
        if (categories && categories.length > 0) {
            setOptions(
                categories.map((category: any) => ({
                    label: category.categoryName,
                    value: category.productCategoryId,
                    categoryDetails: category,
                }))
            );
        }
    };

    const processGiftCardCategories = () => {
        console.log("Processing gift card categories:", giftCardCategories);
        if (giftCardCategories && giftCardCategories.length > 0) {
            const processedOptions = giftCardCategories.map((category: any) => ({
                label: category.categoryName,
                value: category.categoryID,
                giftCardCategoryDetails: category,
            }));
            console.log("Processed gift card category options:", processedOptions);
            setOptions(processedOptions);
        }
    };

    const processCustomerGroups = () => {
        // console.log('processCustomerGroups called with:', customerGroups);
        if (customerGroups && customerGroups.length > 0) {
            const mappedOptions = customerGroups?.map((group: any) => ({
                label: getCustomerGroupDisplayName(group, t),
                value: group.customerGroupId,
                groupDetails: group,
                groupType: group?.groupType,
            }));
            // console.log('Mapped customer group options:', mappedOptions);
            const filteredOptions = mappedOptions?.filter((option: any) => option.groupType != "Custom");
            setOptions(filteredOptions);
        } else {
            // console.log('No customer groups available or empty array');
            setOptions([]);
        }
    };

    const processAllCustomerGroups = () => {
        // console.log('processCustomerGroups called with:', customerGroups);
        if (customerGroups && customerGroups.length > 0) {
            const mappedOptions = customerGroups?.map((group: any) => ({
                label: getCustomerGroupDisplayName(group, t),
                value: group.customerGroupId,
                groupDetails: group,
                // groupType: group?.groupType
            }));
            // console.log('Mapped customer group options:', mappedOptions);
            // const filteredOptions = mappedOptions?.filter((option: any) => option.groupType != "Custom");
            setOptions(mappedOptions);
        } else {
            // console.log('No customer groups available or empty array');
            setOptions([]);
        }
    };

    const processTreeCategories = () => {
        if (categories && categories.length > 0) {
            // Convert nested subCategory structure to flat tree with full path labels
            const flattenCategories = (items: any[], parentPath: string = ""): any[] => {
                let result: any[] = [];
                items.forEach((item: any) => {
                    const currentPath = parentPath 
                        ? `${parentPath} - ${item.categoryName}` 
                        : item.categoryName;
                    
                    result.push({
                        label: currentPath,
                        value: item.productCategoryId,
                        categoryDetails: item,
                    });
                    
                    if (item.subCategory && item.subCategory.length > 0) {
                        result = result.concat(flattenCategories(item.subCategory, currentPath));
                    }
                });
                return result;
            };
            setOptions(flattenCategories(categories));
        }
    };

    const processUnits = () => {
        if (units && units.length > 0) {
            setOptions(
                units.map((unit: any) => ({
                    label: unit.unitName,
                    value: unit.unitCatalogId,
                    unitDetails: unit,
                }))
            );
        }
    };

    const processBrands = () => {
        if (brands && brands.length > 0) {
            setOptions(
                brands.map((brand: any) => ({
                    label: brand.brandName,
                    value: brand.brandId,
                    brandDetails: brand,
                }))
            );
        }
    };

    const processTags = () => {
        if (tags && tags.length > 0) {
            setOptions(
                tags.map((tag: any) => ({
                    label: tag.tagName,
                    value: tag.tagCatalogId,
                    tagDetails: tag,
                }))
            );
        }
    };

    const processVendors = () => {
        if (vendors && vendors.length > 0) {
            setOptions(
                vendors.map((vendor: any) => ({
                    label: vendor.vendorName,
                    value: vendor.vendorId,
                    vendorDetails: vendor,
                }))
            );
        }
    };

    const processVendorsWithNoVendor = () => {
        if (vendors && vendors.length > 0) {
            const vendorOptions = vendors.map((vendor: any) => ({
                label: vendor.vendorName,
                value: vendor.vendorId,
                vendorDetails: vendor,
            }));
            // Add "No Vendor" option at the beginning
            setOptions([
                { label: t("noneOptions.noVendor", { ns: "common" }), value: "no_vendor", vendorDetails: null },
                ...vendorOptions,
            ]);
        }
    };

    const tagswithNoTag = () => {
        if (tags && tags.length > 0) {
            const tagOptions = tags.map((tag: any) => ({
                label: tag.tagName,
                value: tag.tagCatalogId,
                tagDetails: tag,
            }));
            // Add "No Tag" option at the beginning
            setOptions([
                { label: t("noneOptions.noTag", { ns: "common" }), value: "no_tag", tagDetails: null },
                ...tagOptions,
            ]);
        }
    };

    const brandwithNoBrand = () => {
        if (brands && brands.length > 0) {
            const brandOptions = brands.map((brand: any) => ({
                label: brand.brandName,
                value: brand.brandId,
                brandDetails: brand,
            }));
            // Add "No Brand" option at the beginning
            setOptions([
                { label: t("noneOptions.noBrand", { ns: "common" }), value: "no_brand", brandDetails: null },
                ...brandOptions,
            ]);
        }
    };

    const processRoles = () => {
        if (roles && roles.length > 0) {
            // Owner Admin role will be excluded
            setOptions(
                roles.map((role: any) => ({
                    label: getRoleDisplayName(role, t),
                    value: role.roleId,
                    roleDetails: role,
                    isOwnerAdmin: role.isOwnerAdmin,
                }))
            );
        }
    };

    const processWithoutOwnerAdminRoles = () => {
        if (roles && roles.length > 0) {
            // Filter out Owner Admin role
            const filteredRoles = roles.filter((role: any) => !role.isOwnerAdmin);
            setOptions(
                filteredRoles.map((role: any) => ({
                    label: getRoleDisplayName(role, t),
                    value: role.roleId,
                    roleDetails: role,
                }))
            );
        }
    };

    const processPayTypes = () => {
        if (payTypes && payTypes.length > 0) {
            setOptions(
                payTypes.map((payType: any) => ({
                    label: payType.payType,
                    value: payType.payTypeId,
                    payTypeDetails: payType,
                }))
            );
        }
    };

    const processEmployeeTypes = () => {
        if (employeeTypes && employeeTypes.length > 0) {
            setOptions(
                employeeTypes.map((empType: any) => ({
                    label: empType.employeeType,
                    value: empType.employeeTypeId,
                    employeeTypeDetails: empType,
                }))
            );
        }
    };

    const processUsers = () => {
        if (userList && userList.length > 0) {
            setOptions(
                userList.map((user: any) => ({
                    label: `${user.displayName}${user.emailAddress ? ` - ${user.emailAddress}` : ""}`,
                    value: user.userId,
                    userDetails: user,
                }))
            );
        }
    };

    const processWithoutOwnerAdminUsers = () => {
        if (userList && userList.length > 0) {
            const filteredUsers = userList.filter((user: any) => user.roleName != "OwnerAdmin");
            // console.log("user with owner admin filtered out =>", userList.filter((user: any) => user.roleName == "OwnerAdmin"));
            setOptions(
                filteredUsers.map((user: any) => ({
                    label: `${user.displayName}${user.emailAddress ? ` - ${user.emailAddress}` : ""}`,
                    value: user.userId,
                    userDetails: user,
                }))
            );
        }
    }

    const fetchLookupValues = () => {
        setOptions(
            data?.map((lookup: any) => {
                return {
                    label: getLookupDisplayValue(lookup, t),
                    value: lookup.lookupId,
                    lookupDetails: lookup,
                };
            }) || []
        );
    };

    // const processCountries = () => {
    //     if (countries && countries.length > 0) {
    //         setOptions(
    //             countries.map((country: any) => ({
    //                 label: country.label,
    //                 value: country.value,
    //                 countryDetails: country.data,
    //             }))
    //         );
    //     }
    // };

    const fetchCountries = () => {
        commonApi.GetCountryList(
            {},
            (response: any) => {
                console.log("So far coming...", response);
                const countries =
                    response.data?.items
                        .map((country: any) => ({
                            label: getCountryDisplayName(country.countryName, t),
                            value: country.countryId,
                            countryDetails: country,
                        }))
                        .sort((a: any, b: any) => +a.displayOrder - +b.displayOrder) || [];
                setOptions(countries.filter((o: any) => +atob(o.value) === 1));
            },
            (error: any) => {
                console.log("Search error =>", error);
            }
        );
    };

    const processCurrencies = () => {
        setOptions(
            currencyList?.map((curr: any) => {
                return {
                    label: getCurrencyDisplayName(curr.name, t),
                    value: curr.currencyId,
                    lookupDetails: curr,
                };
            }) || []
        );
    };

    const processTimezones = () => {
        setOptions(
            timezoneList?.map((tz: any) => {
                return {
                    label: `${getTimezoneDisplayName(tz?.timezoneName, t)} (${tz?.utcOffset})`,
                    value: tz.timezoneId,
                    timezoneDetails: tz,
                };
            }) || []
        );
    };

    const processReturnReasons = () => {
        if (returnReasons && returnReasons.length > 0) {
            // console.log("Processing return reasons:", returnReasons);
            setOptions(
                returnReasons.map((reason: any) => ({
                    label: reason.reasonName,
                    value: reason.returnPolicyReasonID,
                    reasonDetails: reason,
                }))
            );
        }
    };

    useEffect(() => {
        // console.log('useEffect triggered with type:', type, 'customerGroups:', customerGroups);
        switch (type) {
            case "category":
                processCategories();
                break;
            case "customerGroups":
                // console.log('Processing customerGroups case');
                processCustomerGroups();
                break;
            case "allCustomerGroups":
                processAllCustomerGroups();
                break;
            case "unit":
                processUnits();
                break;
            case "brand":
                processBrands();
                break;
            case "tag":
                processTags();
                break;
            case "vendor":
                processVendors();
                break;
            case "location":
                processLocations();
                break;
            case "allLocations":
                processAllLocations();
                break;
            case "lookup":
                fetchLookupValues();
                break;
            case "country":
                // processCountries();
                fetchCountries();
                break;
            case "categorytreebind":
                processTreeCategories();
                break;
            case "giftCardCategory":
                processGiftCardCategories();
                break;
            case "state":
                //console.log(postoptions);
                //setOptions(postoptions);
                break;
            case "roles":
                processRoles();
                break;
            case "paytypes":
                processPayTypes();
                break;
            case "status":
                // Handled via postoptions prop
                setOptions(postoptions);
                break;
            case "employeeTypes":
                processEmployeeTypes();
                break;
            case "users":
                processUsers();
                break;
            case "currency":
                processCurrencies();
                break;
            case "timezone":
                processTimezones();
                break;
            case "vendorwithnovendor":
                processVendorsWithNoVendor();
                break;
            case "returnReasons":
                processReturnReasons();
                break;
            case "tagwithnotag":
                tagswithNoTag();
                break;
            case "brandwithnobrand":
                brandwithNoBrand();
                break;
            case "withoutOwnerAdminRoles":
                processWithoutOwnerAdminRoles();
                break;
            case "withoutOwnerAdminUsers":
                processWithoutOwnerAdminUsers();
                break;
            default:
                break;
        }
    }, [
        type,
        categories,
        units,
        brands,
        tags,
        vendors,
        locations,
        countries,
        customerGroups,
        roles,
        payTypes,
        giftCardCategories,
        returnReasons,
        employeeTypes,
        userList,
        currencyList,
        timezoneList,
        i18n.language,
    ]);

    // Reset preSelectApplied when preSelectValues changes
    useEffect(() => {
        const currentValue = JSON.stringify(preSelectValues);
        const lastValue = JSON.stringify(lastPreSelectRef.current);
        
        if (currentValue !== lastValue) {
            setPreSelectApplied(false);
            lastPreSelectRef.current = preSelectValues;
        }
    }, [preSelectValues]);

    // Only update when preSelectValues changes, not on every options change
    useEffect(() => {
        // console.log({ preSelectApplied, preSelectValues, options });
        if (preSelectValues && !preSelectApplied && options?.length > 0) {
            let selected;
            if (isMulti && Array.isArray(preSelectValues)) {
                selected = getSelectedOptions(preSelectValues, options);
            } else {
                selected = getSelectedOption(preSelectValues, options);
            }
            // Only update if value is different
            if (JSON.stringify(selected) !== JSON.stringify(control._formValues[name])) {
                // console.log({ name, selected });
                setValue(name, selected);
            }
            setPreSelectApplied(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preSelectValues, options]);

    // The form stores the whole selected option object, including the label as
    // it read at selection time — so a language switch re-translates `options`
    // but leaves the visible selection in the old language until it's re-synced.
    useEffect(() => {
        if (!["lookup", "customerGroups", "allCustomerGroups", "roles", "withoutOwnerAdminRoles", "currency", "timezone", "country", "vendorwithnovendor", "tagwithnotag", "brandwithnobrand"].includes(type) || !options?.length) return;
        const current = selectedValue;
        if (!current) return;

        const refresh = (option: any) =>
            options.find((o: any) => o.value === option?.value) || option;

        if (Array.isArray(current)) {
            const refreshed = current.map(refresh);
            const labelsChanged = refreshed.some(
                (option: any, index: number) => option?.label !== current[index]?.label
            );
            if (labelsChanged) setValue(name, refreshed);
        } else {
            const refreshed = refresh(current);
            if (refreshed?.label !== current?.label) setValue(name, refreshed);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options, selectedValue]);

    useEffect(() => {
        if (type === "state" && postoptions) {
            setOptions(postoptions);
        }
    }, [type, postoptions]);

    const customComponents = showCheckboxes ? {
        Option: CheckboxOption,
    } : {};

    const customStyles = showCheckboxes ? {
        multiValueRemove: (base: any) => ({
            ...base,
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            ':hover': {
                backgroundColor: '#fecaca',
                color: '#b91c1c',
            },
        }),
    } : {};

    return (
        <>
            {topLabel && <Label text={topLabel} />}
            <Controller
                name={name}
                control={control}
                rules={rules}
                render={({ field: { value, onChange }, fieldState }) => (
                    <SelectBox
                        // async={true}
                        value={value as any}
                        onChange={customOnChange || onChange}
                        isMulti={isMulti}
                        isClearable={isClearable}
                        placeholder={placeholder}
                        className={"!my-0 " + className}
                        topParentStyle={topParentStyle}
                        options={options}
                        label={label}
                        error={fieldState.error?.message}
                        onDocument={onDocument}
                        size={size}
                        disabled={disabled}
                        required={required}
                        labelisElipsis={labelisElipsis}
                        hideErrorMessage={hideErrorMessage}
                        customComponents={customComponents}
                        customStyleOption={customStyles}
                    />
                )}
            />
        </>
    );
};


// New custom option component
const CheckboxOption = (props: any) => {
    const { isSelected, children, innerProps } = props;
    
    return (
        <div {...innerProps} className="flex items-center p-2 hover:bg-gray-50">
            <input
                type="checkbox"
                checked={isSelected}
                onChange={() => {}} // Handled by react-select
                className="mr-2"
                readOnly
            />
            <label className="flex-1 cursor-pointer">{children}</label>
        </div>
    );
};

export default CommonDropdown;
